import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { employeesService } from '../services/firebase/employees.service';
import { Employee, EmployeeFormData, DateAvailability, EmployeePreferences } from '../types/employee.types';

/**
 * Custom hook for managing employees
 * Provides CRUD operations with React Query for caching and optimistic updates
 */
export function useEmployees(shopId?: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const organizationId = user?.organizationId;

  // Fetch all employees or employees by shop
  const {
    data: employees = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: shopId ? ['employees', organizationId, 'shop', shopId] : ['employees', organizationId],
    queryFn: async () => {
      if (!organizationId) throw new Error('No organization ID');

      if (shopId) {
        return employeesService.getEmployeesByShop(organizationId, shopId);
      }

      return employeesService.getEmployees(organizationId);
    },
    enabled: !!organizationId,
  });

  // Create employee mutation
  const createEmployeeMutation = useMutation({
    mutationFn: (data: EmployeeFormData) => {
      if (!organizationId) throw new Error('No organization ID');
      return employeesService.createEmployee(organizationId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees', organizationId] });
    },
  });

  // Update employee mutation
  const updateEmployeeMutation = useMutation({
    mutationFn: ({ employeeId, data }: { employeeId: string; data: Partial<EmployeeFormData> }) => {
      if (!organizationId) throw new Error('No organization ID');
      return employeesService.updateEmployee(organizationId, employeeId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees', organizationId] });
    },
  });

  // Delete employee mutation
  const deleteEmployeeMutation = useMutation({
    mutationFn: (employeeId: string) => {
      if (!organizationId) throw new Error('No organization ID');
      return employeesService.deleteEmployee(organizationId, employeeId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees', organizationId] });
    },
  });

  // Update employee status mutation
  const updateEmployeeStatusMutation = useMutation({
    mutationFn: ({
      employeeId,
      status,
    }: {
      employeeId: string;
      status: 'active' | 'inactive';
    }) => {
      if (!organizationId) throw new Error('No organization ID');
      return employeesService.updateEmployeeStatus(organizationId, employeeId, status);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees', organizationId] });
    },
  });

  // Availability mutations
  const updateDateAvailabilityMutation = useMutation({
    mutationFn: ({
      employeeId,
      date,
      availability,
    }: {
      employeeId: string;
      date: string;
      availability: DateAvailability;
    }) => {
      if (!organizationId) throw new Error('No organization ID');
      return employeesService.updateDateAvailability(organizationId, employeeId, date, availability);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees', organizationId] });
    },
  });

  const removeDateAvailabilityMutation = useMutation({
    mutationFn: ({ employeeId, date }: { employeeId: string; date: string }) => {
      if (!organizationId) throw new Error('No organization ID');
      return employeesService.removeDateAvailability(organizationId, employeeId, date);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees', organizationId] });
    },
  });

  // Bulk update availability mutation
  const updateMultipleDateAvailabilityMutation = useMutation({
    mutationFn: ({
      employeeId,
      updates,
    }: {
      employeeId: string;
      updates: Array<{ date: string; availability: DateAvailability | null }>;
    }) => {
      if (!organizationId) throw new Error('No organization ID');
      return employeesService.updateMultipleDateAvailability(organizationId, employeeId, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees', organizationId] });
    },
  });

  // Update preferences mutation
  const updatePreferencesMutation = useMutation({
    mutationFn: ({
      employeeId,
      preferences,
    }: {
      employeeId: string;
      preferences: EmployeePreferences;
    }) => {
      if (!organizationId) throw new Error('No organization ID');
      return employeesService.updatePreferences(organizationId, employeeId, preferences);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees', organizationId] });
    },
  });

  // Helper functions
  const createEmployee = async (data: EmployeeFormData): Promise<string> => {
    return createEmployeeMutation.mutateAsync(data);
  };

  const updateEmployee = async (employeeId: string, data: Partial<EmployeeFormData>): Promise<void> => {
    return updateEmployeeMutation.mutateAsync({ employeeId, data });
  };

  const deleteEmployee = async (employeeId: string): Promise<void> => {
    return deleteEmployeeMutation.mutateAsync(employeeId);
  };

  const updateEmployeeStatus = async (employeeId: string, status: 'active' | 'inactive'): Promise<void> => {
    return updateEmployeeStatusMutation.mutateAsync({ employeeId, status });
  };

  const getEmployee = (employeeId: string): Employee | undefined => {
    return employees.find((employee) => employee.id === employeeId);
  };

  const updateDateAvailability = async (
    employeeId: string,
    date: string,
    availability: DateAvailability
  ): Promise<void> => {
    return updateDateAvailabilityMutation.mutateAsync({ employeeId, date, availability });
  };

  const removeDateAvailability = async (employeeId: string, date: string): Promise<void> => {
    return removeDateAvailabilityMutation.mutateAsync({ employeeId, date });
  };

  const updateMultipleDateAvailability = async (
    employeeId: string,
    updates: Array<{ date: string; availability: DateAvailability | null }>
  ): Promise<void> => {
    return updateMultipleDateAvailabilityMutation.mutateAsync({ employeeId, updates });
  };

  const updatePreferences = async (
    employeeId: string,
    preferences: EmployeePreferences
  ): Promise<void> => {
    return updatePreferencesMutation.mutateAsync({ employeeId, preferences });
  };

  return {
    // Data
    employees,
    isLoading,
    error,

    // Functions
    createEmployee,
    updateEmployee,
    deleteEmployee,
    updateEmployeeStatus,
    getEmployee,
    refetch,

    // Availability functions
    updateDateAvailability,
    removeDateAvailability,
    updateMultipleDateAvailability,
    updatePreferences,

    // Mutation states
    isCreating: createEmployeeMutation.isPending,
    isUpdating: updateEmployeeMutation.isPending,
    isDeleting: deleteEmployeeMutation.isPending,
  };
}
